import { CategoryTabIds } from '@shared/constants';
import { gridConfig } from '@client/config/grid';
import { Campaign } from '@shared/types/mylounge';
import { Grid, GridBlock, ColumnSize } from '@client/config/layout';

import { OpenGridWithShowstopper } from '@client/config/layouts';
import { OpenGridWithShowstopperAndNewDealsCarousel } from '@client/config/new-deals-layouts';

export const getNextGridBlock = (gridBlock: GridBlock, columnCount: number): GridBlock => {
    const newX = (gridBlock.x + 1) % columnCount;
    const newY = gridBlock.x + 1 >= columnCount ? gridBlock.y + 1 : gridBlock.y;

    const newGridBlock = {
        w: 1,
        h: 1,
        x: newX,
        y: newY,
    };

    return newGridBlock;
};

export const getGridConfig = (
    categoryTabId: CategoryTabIds,
    hasAdditionalShowStopper?: boolean,
): Grid => {
    if (categoryTabId === CategoryTabIds.ALL) {
        // both default and ALL has same grid. But keeping for future changes
        return hasAdditionalShowStopper
            ? OpenGridWithShowstopperAndNewDealsCarousel
            : OpenGridWithShowstopper;
    }
    return OpenGridWithShowstopper;
};

export const getGrid = (
    categoryTabId: CategoryTabIds,
    columnCount: ColumnSize,
    campaignsCount: number,
    preprocessGrid?: (grid: GridBlock[]) => number,
    isNewDealsToday?: boolean,
): GridBlock[] => {
    const categoryGridConfig = getGridConfig(categoryTabId, isNewDealsToday);
    const grid = categoryGridConfig[columnCount];

    const numberOfCampaignsAdded = preprocessGrid?.(grid) || 0;
    const count = campaignsCount + numberOfCampaignsAdded;

    while (grid.length <= count) {
        grid.push(getNextGridBlock(grid[grid.length - 1], columnCount));
    }

    return grid;
};

export const mergeCampaignsWithGrid = (
    campaigns: Campaign[],
    gridBlocks: GridBlock[],
    columnCount: number,
): (GridBlock | (Campaign & GridBlock))[] => {
    let customElementOffset = 0;

    const result = campaigns.reduce((accumulator, campaign, index) => {
        let currentGridBlock = gridBlocks[index + customElementOffset];

        if (currentGridBlock?.type) {
            const showstopperBlock = { ...currentGridBlock };
            customElementOffset++;

            // Generate new gridBlock if missing (on-demand generation)
            let nextGridBlock = gridBlocks[index + customElementOffset];
            if (!nextGridBlock && gridBlocks.length > 0) {
                nextGridBlock = getNextGridBlock(gridBlocks[gridBlocks.length - 1], columnCount);
                gridBlocks.push(nextGridBlock);
            }

            return [
                ...accumulator,
                showstopperBlock,
                {
                    ...campaign,
                    ...nextGridBlock,
                },
            ];
        }

        // Ensure gridBlock exists for regular campaigns too
        if (!currentGridBlock && gridBlocks.length > 0) {
            currentGridBlock = getNextGridBlock(gridBlocks[gridBlocks.length - 1], columnCount);
            gridBlocks.push(currentGridBlock);
        }

        return [
            ...accumulator,
            {
                ...campaign,
                ...currentGridBlock,
            },
        ];
    }, []);

    return result;
};

export const getMaxColumnHeight = (gridBlocks: GridBlock[]) => {
    let maxHeight = 0;

    gridBlocks.forEach((block: GridBlock) => {
        const sum = block.y + block.h;
        maxHeight = Math.max(sum, maxHeight);
    });

    return maxHeight;
};

export const getColumnCount = (width: number) => {
    const { gridCalculation, columns, minWidth } = gridConfig.open;

    const availableColumnCount = 1;
    for (let i = 0; i < columns.length; i++) {
        const colCount: ColumnSize = columns[i];
        const newWidth = (minWidth * 100) / gridCalculation[colCount].column;
        if (newWidth <= width) {
            return colCount;
        }
    }

    return availableColumnCount;
};
